import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo, useRef } from "react";
import { Music, RotateCcw, Trash2, TableProperties, Sparkles, Radio, UserPlus, MessageSquare, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { MessageNotificationProvider, useMessageNotifications } from "@/hooks/use-message-notifications";
import ParticipantStats from "@/components/participant-stats";
import UnifiedParticipantsTable from "@/components/unified-participants-table";
import UnifiedOnStageTable from "@/components/unified-on-stage-table";
import SelectedForStageTable from "@/components/selected-for-stage-table";
import ConsiderationTable from "@/components/consideration-table";
import AddParticipantModal from "@/components/add-participant-modal";
import JamMessaging from "@/components/jam-messaging";
import RaffleModal from "@/components/raffle-modal";
import { PageBackground } from "@/components/ui/page-background";
import { TrackerHeader } from "@/components/tracker-header";
import type { Performer, JamNight } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import SetTimeLimitModal from "@/components/set-time-limit-modal";
import { EventActionsDropdown } from "@/components/event-actions-dropdown";
import { useStablePerformers } from "@/hooks/use-stable-performers";
import { useLocation } from "wouter";
import { EventNotificationSettings } from "@/components/event-notification-settings";

interface JamTrackerProps {
  jamNightId: number;
  activeTab?: string;
}


export default function JamTracker(props: JamTrackerProps) {
  return (
    <MessageNotificationProvider jamNightId={props.jamNightId}>
      <JamTrackerContent {...props} />
    </MessageNotificationProvider>
  );
}

function JamTrackerContent({ jamNightId, activeTab: urlActiveTab }: JamTrackerProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTimeLimitModal, setShowTimeLimitModal] = useState(false);
  const [showRaffleModal, setShowRaffleModal] = useState(false);
  const [showClearOffStageConfirm, setShowClearOffStageConfirm] = useState(false);
  const [showResetAllConfirm, setShowResetAllConfirm] = useState(false);
  
  // Load kanban settings from localStorage
  const [isKanbanView, setIsKanbanView] = useState(() => {
    const saved = localStorage.getItem('jam-tracker-kanban-view');
    return saved === 'true';
  });
  const [showParticipantsInKanban, setShowParticipantsInKanban] = useState(() => {
    const saved = localStorage.getItem('jam-tracker-show-participants');
    return saved === 'true';
  });
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const messagingPanelRef = useRef<HTMLDivElement>(null);
  // @ts-ignore
  const { hasNewMessages, setIsMessagingOpen, currentTab, setCurrentTab } = useMessageNotifications();

  // Use currentTab from context as the source of truth
  const selectedTab = currentTab;

  // Sync tab changes with context and URL
  const handleTabChange = (value: string) => {
    setCurrentTab(value);
    if (value === "chat") {
      setIsMessagingOpen(true);
    }
    // Update URL when tab changes
    setLocation(`/event/${jamNightId}/${value}`);
  };

  // Save kanban view preference and manage body overflow
  useEffect(() => {
    localStorage.setItem('jam-tracker-kanban-view', isKanbanView.toString());
    
    // Prevent body scroll when in kanban view
    if (isKanbanView && selectedTab === 'live') {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isKanbanView, selectedTab]);

  // Save participants visibility preference
  useEffect(() => {
    localStorage.setItem('jam-tracker-show-participants', showParticipantsInKanban.toString());
  }, [showParticipantsInKanban]);

  // Initialize tab from URL or use default
  useEffect(() => {
    if (urlActiveTab) {
      // Valid tabs for jam tracker
      const validTabs = ["live", "signups", "chat", "manage"];
      if (validTabs.includes(urlActiveTab)) {
        setCurrentTab(urlActiveTab);
        if (urlActiveTab === "chat") {
          setIsMessagingOpen(true);
        }
      } else {
        // Invalid tab in URL, replace URL without creating history entry
        window.history.replaceState(null, '', `/event/${jamNightId}/signups`);
        setCurrentTab("signups");
      }
    } else {
      // No tab in URL, replace URL without creating history entry
      window.history.replaceState(null, '', `/event/${jamNightId}/signups`);
      setCurrentTab("signups");
    }
  }, [urlActiveTab, jamNightId, setCurrentTab, setIsMessagingOpen]);

  const showMultiScreenLayout = user?.multiScreenJams;

  const { data: jamNight, isLoading: jamNightLoading } = useQuery<JamNight>({
    queryKey: [`/api/events/${jamNightId}`],
  });

  const { data: rawPerformers = [], isLoading: performersLoading } = useQuery<
    Performer[]
  >({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
  });

  // Stabilize performers array to prevent unnecessary re-renders of child components
  const performers = useStablePerformers(rawPerformers);

  const resetAllPerformersMutation = useMutation({
    mutationFn: async () => {
      // Sort performers alphabetically by name
      const sortedPerformers = [...performers].sort((a, b) => 
        a.name.localeCompare(b.name)
      );

      // Create reset promises with new sort orders
      const resetPromises = sortedPerformers.map((performer, index) =>
        apiRequest("PATCH", `/api/performers/${performer.id}`, {
          played: false,
          timeOnStage: 0,
          status: 'participant',
          stageStartTime: null,
          currentInstruments: [],
          replacementName: null,
          replacingPerformerId: null,
          performanceCount: 0, // Reset performance count
          sortOrder: (index + 1) * 1000, // Assign sort orders with gaps of 1000
        })
      );
      await Promise.all(resetPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
      toast({
        title: "Success",
        description: "All participants have been reset successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to reset participants. Please try again.",
        variant: "destructive",
      });
    },
  });

  const clearOffStagePerformersMutation = useMutation({
    mutationFn: async () => {
      const onStagePerformers = performers.filter(performer => 
        performer.status === 'on_stage' || performer.status === 'pending_removal'
      );
      const updatePromises = onStagePerformers.map(performer =>
        apiRequest("PATCH", `/api/performers/${performer.id}`, {
          status: 'participant',
          stageStartTime: null,
          currentInstruments: [],
          replacementName: null,
        })
      );
      
      // Also clear replacement targeting data from any performers targeting the cleared performers
      const clearReplacementPromises = performers
        .filter(p => onStagePerformers.some(onStagePerformer => onStagePerformer.id === p.replacingPerformerId))
        .map(performer =>
          apiRequest("PATCH", `/api/performers/${performer.id}`, {
            replacingPerformerId: null,
          })
        );
      
      await Promise.all([...updatePromises, ...clearReplacementPromises]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
      toast({
        title: "Success",
        description: "All performers have been taken off stage successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to clear stage. Please try again.",
        variant: "destructive",
      });
    },
  });

  const userRole = user?.role === 'god' 
    ? 'god' 
    : user?.organisationUsers?.find(
        (ou) => ou.organisation.id === jamNight?.organisationId
      )?.role;

  const isLoading = jamNightLoading || performersLoading;
  const isEventCompleted = jamNight?.completed || false;
  
  // Check if there are people on stage for live indicator
  const hasPerformersOnStage = useMemo(() => 
    performers.some(p => (p.status === 'on_stage' || p.status === 'pending_removal') && !p.removed), [performers]);

  const RenderManagementSection = () => (
    <>
      <div className="flex justify-center items-center mb-4">
        <h1 className="text-2xl font-bold text-slate-800 drop-shadow-sm text-center">{jamNight?.name}</h1>
      </div>
      <ParticipantStats performers={performers} />

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Button
          variant="outline"
          disabled={isEventCompleted || clearOffStagePerformersMutation.isPending || performers.filter(p => p.status === 'on_stage' || p.status === 'pending_removal').length === 0}
          onClick={() => setShowClearOffStageConfirm(true)}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Clear All Off Stage
        </Button>
        <ConfirmationDialog
          isOpen={showClearOffStageConfirm}
          onOpenChange={setShowClearOffStageConfirm}
          title="Clear All Off Stage?"
          description="This will take all performers currently on stage and move them to off stage. Their performance time will be calculated and saved. This action cannot be undone."
          onConfirm={() => clearOffStagePerformersMutation.mutate()}
          confirmText="Yes, Clear Stage"
          isConfirming={clearOffStagePerformersMutation.isPending}
        />

        {(userRole === 'owner' || userRole === 'god') && (
          <Button
            variant="outline"
            onClick={() => setShowTimeLimitModal(true)}
            disabled={isEventCompleted}
            className="disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Set Time Limit
          </Button>
        )}

        <Button
          variant="outline"
          disabled={isEventCompleted || resetAllPerformersMutation.isPending || performers.length === 0}
          onClick={() => setShowResetAllConfirm(true)}
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset All Participants
        </Button>
        <ConfirmationDialog
          isOpen={showResetAllConfirm}
          onOpenChange={setShowResetAllConfirm}
          title="Reset All Participants?"
          description={`This will reset all participants back to "not played" status and clear their stage time. Anyone currently on stage or selected for stage will be reset. All replacement information will be cleared. This action cannot be undone.`}
          onConfirm={() => resetAllPerformersMutation.mutate()}
          confirmText="Yes, Reset All"
          isConfirming={resetAllPerformersMutation.isPending}
        />

        <Button
          variant="outline"
          onClick={() => setLocation(`/event/${jamNightId}/participants`)}
        >
          <TableProperties className="h-4 w-4 mr-2" />
          View All Participant Data
        </Button>

        {jamNight?.raffleEnabled && (
          <Button
            variant="outline"
            className="bg-yellow-100/40 backdrop-blur-md border-yellow-200/50 text-yellow-700 hover:bg-yellow-100/60 hover:border-yellow-300/60 shadow-lg transition-all duration-300"
            style={{
              boxShadow: '0 4px 16px rgba(251,191,36,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}
            onClick={() => setShowRaffleModal(true)}
            disabled={isEventCompleted || performers.filter(p => !p.removed && (p.raffleEntrances || 0) > 0).length === 0}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Run Raffle
          </Button>
        )}
      </div>

      {/* Event Management Dropdown - Full width on mobile */}
      {jamNight && (userRole === 'owner' || userRole === 'god') && (
        <div className="mt-4 sm:flex sm:justify-center">
          <EventActionsDropdown
            jamNight={jamNight}
            canManageEvent={true}
            variant="button"
            className="w-full sm:w-auto"
          />
        </div>
      )}

      {/* Notification Settings */}
      {jamNight && (
        <EventNotificationSettings eventId={jamNightId} eventName={jamNight.name} />
      )}
    </>
  );

  if (isLoading) {
    return (
      <PageBackground className="flex items-center justify-center">
        <div className="text-center relative z-10">
          <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700 drop-shadow-sm" />
          <p className="text-slate-700 drop-shadow-sm">Loading jam night data...</p>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      
      <TrackerHeader
        isEventCompleted={isEventCompleted}
        onAddParticipant={() => setShowAddModal(true)}
        showMessageNotification={true}
        onMessageNotificationClick={() => {
          if (user?.multiScreenJams) {
            setCurrentTab("chat");
          } else {
            setIsMessagingOpen(true);
            setTimeout(() => {
              messagingPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          }
        }}
        hasNewMessages={hasNewMessages}
        showKanbanControls={selectedTab === "live"}
        isKanbanView={isKanbanView}
        onToggleKanbanView={() => setIsKanbanView(!isKanbanView)}
        showParticipantsInKanban={showParticipantsInKanban}
        onToggleParticipants={() => setShowParticipantsInKanban(!showParticipantsInKanban)}
      />

      <main className="mx-auto px-4 sm:px-6 lg:px-32 xl:px-48 pt-4 pb-6 relative z-10">
        {!showMultiScreenLayout && <div ref={messagingPanelRef}><JamMessaging jamNightId={jamNightId} disabled={isEventCompleted} /></div>}

        {showMultiScreenLayout ? (
            <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-white/30 backdrop-blur-md border border-white/20 shadow-lg p-1">
                <TabsTrigger 
                  value="live"
                  className="data-[state=active]:bg-white/40 data-[state=active]:text-blue-600 data-[state=active]:font-semibold data-[state=active]:shadow-md transition-all duration-300 flex items-center gap-2"
                >
                  <Radio className={`h-4 w-4 ${hasPerformersOnStage ? 'text-green-500 animate-pulse' : ''}`} />
                  Live
                </TabsTrigger>
                <TabsTrigger 
                  value="signups"
                  className="data-[state=active]:bg-white/40 data-[state=active]:text-blue-600 data-[state=active]:font-semibold data-[state=active]:shadow-md transition-all duration-300 flex items-center gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  List
                </TabsTrigger>
                <TabsTrigger 
                  value="chat"
                  className="data-[state=active]:bg-white/40 data-[state=active]:text-blue-600 data-[state=active]:font-semibold data-[state=active]:shadow-md transition-all duration-300 flex items-center gap-2"
                >
                  {hasNewMessages && selectedTab !== "chat" ? (
                    <Badge
                      variant="destructive"
                      className="h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs bg-red-500/80 backdrop-blur-sm border border-red-400/30"
                    >
                      !
                    </Badge>
                  ) : (
                    <MessageSquare className="h-4 w-4" />
                  )}
                  Chat
                </TabsTrigger>
                <TabsTrigger 
                  value="manage"
                  className="data-[state=active]:bg-white/40 data-[state=active]:text-blue-600 data-[state=active]:font-semibold data-[state=active]:shadow-md transition-all duration-300 flex items-center gap-2"
                >
                  <Settings className="h-4 w-4" />

                </TabsTrigger>
              </TabsList>

              <TabsContent value="live" className="mt-4">
                {isKanbanView ? (
                  /* Kanban View - Full height with scrollable columns */
                  <div 
                    className={`grid grid-cols-1 ${showParticipantsInKanban ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4 -mx-4 sm:-mx-6 lg:-mx-32 xl:-mx-48 px-4`}
                    style={{ height: 'calc(100vh - 200px)' }}
                  >
                    {/* Column 1: Participants (conditionally shown) */}
                    {showParticipantsInKanban && (
                      <div className="lg:col-span-1 flex flex-col h-full overflow-hidden">
                        <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-2" style={{ minHeight: 0 }}>
                          <UnifiedParticipantsTable
                            performers={performers}
                            jamNightId={jamNightId}
                            jamNight={jamNight}
                            mode="jam-night"
                            disabled={isEventCompleted}
                            forceMobileView={true}
                          />
                        </div>
                      </div>
                    )}

                    {/* Column 2: Consideration */}
                    <div className="lg:col-span-1 flex flex-col h-full overflow-hidden">
                      <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-2" style={{ minHeight: 0 }}>
                        <ConsiderationTable
                          performers={performers}
                          jamNightId={jamNightId}
                          disabled={isEventCompleted}
                          forceMobileView={true}
                        />
                      </div>
                    </div>

                    {/* Column 3: Up Next */}
                    <div className="lg:col-span-1 flex flex-col h-full overflow-hidden">
                      <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-2" style={{ minHeight: 0 }}>
                        <SelectedForStageTable
                          performers={performers}
                          jamNightId={jamNightId}
                          disabled={isEventCompleted}
                          forceMobileView={true}
                        />
                      </div>
                    </div>

                    {/* Column 4: On Stage */}
                    <div className="lg:col-span-1 flex flex-col h-full overflow-hidden">
                      <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-2" style={{ minHeight: 0 }}>
                        {jamNight && (
                          <UnifiedOnStageTable
                            performers={performers}
                            jamNightId={jamNightId}
                            jamNight={jamNight}
                            disabled={isEventCompleted}
                            mode="jam-night"
                            forceMobileView={true}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Standard View */
                  (() => {
                    const onStagePerformers = performers.filter(p => (p.status === 'on_stage' || p.status === 'pending_removal') && !p.removed);
                    const selectedForStagePerformers = performers.filter(p => p.status === 'selected' && !p.removed);
                    const hasLiveContent = onStagePerformers.length > 0 || selectedForStagePerformers.length > 0;

                    if (!hasLiveContent) {
                      return (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                          <Music className="h-12 w-12 text-slate-400 mb-4" />
                          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Performers On Stage</h3>
                          <p className="text-slate-600 max-w-md">
                            Select participants from the List tab to move them on stage or prepare them for staging.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <>
                        {jamNight && <UnifiedOnStageTable performers={performers} jamNightId={jamNightId} jamNight={jamNight} disabled={isEventCompleted} mode="jam-night" />}
                        <SelectedForStageTable performers={performers} jamNightId={jamNightId} disabled={isEventCompleted} />
                        <ConsiderationTable performers={performers} jamNightId={jamNightId} disabled={isEventCompleted} />
                      </>
                    );
                  })()
                )}
              </TabsContent>

              <TabsContent value="signups" className="mt-4 space-y-4">
                <UnifiedParticipantsTable performers={performers} jamNightId={jamNightId} jamNight={jamNight} disabled={isEventCompleted} mode="jam-night" />
              </TabsContent>

              <TabsContent value="manage" className="mt-4 space-y-4">
                <RenderManagementSection />
              </TabsContent>

              <TabsContent value="chat" className="mt-4 space-y-4">
                <JamMessaging jamNightId={jamNightId} disabled={isEventCompleted} forceOpen={selectedTab === "chat"} />
              </TabsContent>
            </Tabs>
        ) : (
            <div className="space-y-4">
              {jamNight && <UnifiedOnStageTable performers={performers} jamNightId={jamNightId} jamNight={jamNight} disabled={isEventCompleted} mode="jam-night" />}
              <SelectedForStageTable performers={performers} jamNightId={jamNightId} disabled={isEventCompleted} />
              <ConsiderationTable performers={performers} jamNightId={jamNightId} disabled={isEventCompleted} />
              <UnifiedParticipantsTable performers={performers} jamNightId={jamNightId} jamNight={jamNight} disabled={isEventCompleted} mode="jam-night" />
              <RenderManagementSection />
            </div>
        )}
      </main>

      {jamNight && (
        <SetTimeLimitModal
          isOpen={showTimeLimitModal}
          onOpenChange={setShowTimeLimitModal}
          jamNight={jamNight}
        />
      )}

      <AddParticipantModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        jamNightId={jamNightId}
      />

      <RaffleModal
        open={showRaffleModal}
        onOpenChange={setShowRaffleModal}
        performers={performers.filter((p) => !p.removed)}
        jamNightId={jamNightId}
      />
    </PageBackground>
  );
}